import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import type { Map, MapProps, Viewport } from "react-leaflet"

import { Client, Driver, Order, RootState } from "@/types"
import { OrderUtils } from "@/utils"
import { DriversPanelTab } from "@/views/MapView/partials/Drivers/DriversPanel"
import { HereLocation } from "@/views/MapView/partials/Map/MapSearch/HereLocation"
import { OrdersPanelTab } from "@/views/MapView/partials/Orders/OrdersPanel"

import { setDate } from "./global"
import { setCurrentTeam } from "./teams"
import { clearDataOnLogout } from "./user"

type UiState = {
  aboutPanel: { type: "order" | "client"; id: Order["id"] | Client["id"] } | false
  panelHidden: { left: boolean; right: boolean }
  showOrderDetails: { id: Order["id"]; type: "pickup" | "delivery" } | false
  showDriverDetails: Driver["id"] | false
  driverIdTasksRoute: Driver["id"] | null
  driverIdTasksRouteHistory: Driver["id"] | null
  focusedDriverId: Driver["id"] | null
  focusedOrderId: Order["id"] | null
  focusedOrderArea: Array<{
    id: Order["id"]
    pickup: { lat: number; lng: number } | null
    delivery: { lat: number; lng: number } | null
  }> | null
  isOrdersPanelSearchVisible: boolean
  ordersPanelSearchValue: string
  isDriversPanelSearchVisible: boolean
  driversPanelSearchValue: string
  driversPanelTab: DriversPanelTab
  ordersPanelTab: OrdersPanelTab
  isTeamsModalOpen: boolean
  isMapSearchOpen: boolean
  pinnedLocation: HereLocation | null
  mapViewport: Viewport | null
  clientsSearchValue: string
}

const initialState: UiState = {
  aboutPanel: false,
  panelHidden: { left: false, right: false },
  showOrderDetails: false,
  showDriverDetails: false,
  driverIdTasksRoute: null,
  driverIdTasksRouteHistory: null,
  focusedDriverId: null,
  focusedOrderId: null,
  focusedOrderArea: null,
  isOrdersPanelSearchVisible: false,
  ordersPanelSearchValue: "",
  isDriversPanelSearchVisible: false,
  driversPanelSearchValue: "",
  driversPanelTab: "free",
  ordersPanelTab: "assigned",
  isTeamsModalOpen: false,
  isMapSearchOpen: false,
  pinnedLocation: null,
  mapViewport: null,
  clientsSearchValue: "",
}

export const setShowOrderDetails = createAsyncThunk(
  "ui/setShowOrderDetails",
  (arg: { id: Order["id"]; type?: "pickup" | "delivery" } | false, { getState }) => {
    if (!arg) return false

    const order = (getState() as RootState).orders.entities[arg.id]

    if (!arg.type && order) {
      const currentPointType = OrderUtils.getCurrentPointType(order)
      return { id: arg.id, type: currentPointType as "pickup" | "delivery" }
    }

    return arg as UiState["showOrderDetails"]
  }
)

const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    resetToInitialState: () => initialState,
    setPanelHidden(state, action: PayloadAction<{ left: boolean; right: boolean }>) {
      state.panelHidden = action.payload
    },
    setAboutPanel(state, action: PayloadAction<{ type: "order" | "client"; id: Order["id"] | Client["id"] } | false>) {
      state.aboutPanel = action.payload
    },
    setShowDriverDetails(state, action: PayloadAction<Driver["id"] | false>) {
      state.showDriverDetails = action.payload
    },
    setDriverIdTasksRoute(state, action: PayloadAction<Driver["id"] | null>) {
      state.driverIdTasksRouteHistory = null
      state.driverIdTasksRoute = action.payload
    },
    setDriverIdTasksRouteHistory(state, action: PayloadAction<Driver["id"] | null>) {
      state.driverIdTasksRoute = null
      state.driverIdTasksRouteHistory = action.payload
    },
    setFocusedDriver(state, action: PayloadAction<Driver["id"] | null>) {
      state.focusedDriverId = action.payload
    },
    setFocusedOrder(state, action: PayloadAction<Order["id"] | null>) {
      state.focusedOrderId = action.payload
    },
    setFocusedOrderArea(
      state,
      action: PayloadAction<Array<{
        id: Order["id"]
        pickup: { lat: number; lng: number } | null
        delivery: { lat: number; lng: number } | null
      }> | null>
    ) {
      state.focusedOrderArea = action.payload
    },
    setOrdersPanelSearchVisibility(state, action: PayloadAction<boolean>) {
      state.isOrdersPanelSearchVisible = action.payload
    },
    setOrdersPanelSearchValue(state, action: PayloadAction<string>) {
      state.ordersPanelSearchValue = action.payload
    },
    setDriversPanelSearchVisibility(state, action: PayloadAction<boolean>) {
      state.isDriversPanelSearchVisible = action.payload
    },
    setDriversPanelSearchValue(state, action: PayloadAction<string>) {
      state.driversPanelSearchValue = action.payload
    },
    setDriversPanelTab(state, action: PayloadAction<DriversPanelTab>) {
      state.driversPanelTab = action.payload
    },
    setOrdersPanelTab(state, action: PayloadAction<OrdersPanelTab>) {
      state.ordersPanelTab = action.payload
    },
    setTeamsModalVisibility(state, action: PayloadAction<boolean>) {
      state.isTeamsModalOpen = action.payload
    },
    setMapSearchVisibility(state, action: PayloadAction<boolean>) {
      state.isMapSearchOpen = action.payload
    },
    setPinnedLocation(state, action: PayloadAction<HereLocation | null>) {
      state.pinnedLocation = action.payload
    },
    saveMapViewport(state, action: PayloadAction<Viewport | null>) {
      state.mapViewport = action.payload
    },
    setClientsSearchValue(state, action: PayloadAction<string>) {
      state.clientsSearchValue = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setShowOrderDetails.fulfilled, (state, { payload }) => {
      state.showOrderDetails = payload
    })
    builder.addCase(setDate, () => initialState)
    builder.addCase(setCurrentTeam, () => initialState)
    builder.addCase(clearDataOnLogout, () => initialState)
  },
})

export const retrieveSavedMapViewport = createAsyncThunk(
  "ui/setMapViewport",
  async (map: Map<MapProps, L.Map> | null | undefined, { getState, dispatch, rejectWithValue }) => {
    const {
      ui: { mapViewport },
    } = getState() as RootState

    if (!mapViewport) return rejectWithValue("No saved mapViewport")

    const { center, zoom } = mapViewport

    if (!center || !zoom) return rejectWithValue("Incorrect saved mapViewport")

    map?.leafletElement?.flyTo(center, zoom, { animate: false })
    dispatch(saveMapViewport(null))
  }
)

export const {
  resetToInitialState,
  setAboutPanel,
  setFocusedDriver,
  setFocusedOrder,
  setFocusedOrderArea,
  setPanelHidden,
  setShowDriverDetails,
  setDriverIdTasksRoute,
  setDriverIdTasksRouteHistory,
  setTeamsModalVisibility,
  setOrdersPanelSearchVisibility,
  setOrdersPanelSearchValue,
  setDriversPanelSearchVisibility,
  setDriversPanelSearchValue,
  setDriversPanelTab,
  setOrdersPanelTab,
  setMapSearchVisibility,
  setPinnedLocation,
  saveMapViewport,
  setClientsSearchValue,
} = uiSlice.actions

export const uiReducer = uiSlice.reducer
